import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { EventViewModel } from '../../../models-views/event.view.model';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { EventDashboardStatsModel } from '../../../models/event.model';
import { QuickAddService } from '../../../services/quick-add.service';
import { EventModeratorsService } from '../../../services/event-moderators.service';
import { AppCommon } from '../../../providers/app-common';
import LoginModel from '../../../models/user.model';

@Component({
  selector: 'app-cet-dashboard',
  standalone: false,
  templateUrl: './cet-dashboard.component.html',
  styleUrl: './cet-dashboard.component.scss'
})
export class CetDashboardComponent {
  @Input() tabData: any;
  stats = [];
  destroy$ = new Subject()
  outsourcingItems = [];
  @Output() selectedIndex = new EventEmitter<number>();
  eventId: number | null = null;
  eventDashboardStatsModel = new BehaviorSubject<EventDashboardStatsModel>(
    new EventDashboardStatsModel()
  );

  // External Moderators
  moderators: any[] = [];
  allModerators: any[] = [];
  moderatorsLoading = false;
  moderatorModalVisible = false;
  moderatorSaving = false;
  editingModerator: any = null;
  loginModel = new BehaviorSubject<LoginModel>(new LoginModel());

  get linkedModeratorIds(): number[] {
    return (this.moderators || []).map((m: any) => m.id);
  }

  constructor(
    private modalService: NzModalService,
    private message: NzMessageService,
    private vm: EventViewModel,
    private route: ActivatedRoute,
    private router: Router,
    private quickAddService: QuickAddService,
    private moderatorService: EventModeratorsService
  ) {
    if (this.router.url.includes('/app/events')) {
      const pageID = Number(this.route.snapshot.paramMap.get('id'));
      this.tabData = { id: pageID, name: `events-dashboard-${pageID}` }
    }

    this.quickAddService.refreshData.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (data && this.tabData?.id) {
        this.vm.getPerEventDashboardStats(this.tabData.id);
        this.loadModerators();
      }
    });
  }

  ngOnInit(): void {
    this.loginModel.next(AppCommon.getloggedInObject());
    this.vm.parentCetDashboardComponent = this;
    this.vm.subscribeToDashboardStats(this.eventDashboardStatsModel);
    this.vm.getPerEventDashboardStats(this.tabData.id);
    this.loadAllModerators();
    this.loadModerators();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  toStatRedirect(e: any) {
    if (e.label === 'Pending Tasks') { this.selectedIndex.emit(1); }
    if (e.label === 'Quotation') { this.selectedIndex.emit(7); }
    if (e.label === 'Payments') { this.selectedIndex.emit(9); }
  }

  toOutsourceRedirect(e: any) {
    if (e) { this.selectedIndex.emit(4); }
  }

  dataReceived() {
    const currencyItems = ['Payments', 'Budget'];
    const masterStats = this.eventDashboardStatsModel.value.stats.map(element => ({
      label: element.name,
      value: currencyItems.includes(element.name) ? `$${element.value}` : element.value,
      valueColor: '#4e47e6'
    }));

    function toPascalCase(text: string): string {
      return text.toLowerCase().split(/[^a-zA-Z0-9]+/).filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('');
    }

    const outsourcingStats = this.eventDashboardStatsModel.value.outsourcing.map(element => ({
      label: element.name,
      status: toPascalCase(element.value.toString())
    }));

    this.stats = masterStats;
    this.outsourcingItems = outsourcingStats;
  }

  // ── Moderator CRUD ──────────────────────────────────────────────

  loadModerators(): void {
    this.moderatorsLoading = true;
    this.moderatorService.getModerators(this.tabData.id).subscribe({
      next: (data: any) => {
        this.moderators = data?.results ?? (Array.isArray(data) ? data : []);
        this.moderatorsLoading = false;
      },
      error: () => { this.moderatorsLoading = false; }
    });
  }

  loadAllModerators(search: string = ''): void {
    this.moderatorService.getAllModerators(1, 300, search).subscribe({
      next: (data: any) => {
        this.allModerators = data?.results ?? (Array.isArray(data) ? data : []);
      }
    });
  }

  openModeratorModal(moderator?: any): void {
    this.editingModerator = moderator ?? null;
    this.moderatorModalVisible = true;
  }

  closeModeratorModal(): void {
    this.moderatorModalVisible = false;
    this.editingModerator = null;
  }

  onModeratorSave(formValue: any): void {
    this.moderatorSaving = true;
    const userId: number = this.loginModel.value?.user?.id;

    if (!this.editingModerator && formValue?.mode === 'existing' && formValue?.existing_moderator_id) {
      const existingId = Number(formValue.existing_moderator_id);
      const currentIds = this.moderators.map(m => m.id);

      if (currentIds.includes(existingId)) {
        this.message.warning('Moderator is already linked to this event');
        this.moderatorSaving = false;
        return;
      }

      this.moderatorService.updateEventModerators(this.tabData.id, [...currentIds, existingId], userId).subscribe({
        next: () => {
          this.message.success('Existing moderator linked');
          this.moderatorSaving = false;
          this.closeModeratorModal();
          this.loadModerators();
        },
        error: () => {
          this.message.error('Failed to link existing moderator');
          this.moderatorSaving = false;
        }
      });
      return;
    }

    const payload = {
      name: formValue?.name,
      company: formValue?.company,
      job_title: formValue?.job_title,
      email: formValue?.email,
      phone_number: formValue?.phone_number,
      linkedin_url: formValue?.linkedin_url
    };

    if (this.editingModerator) {
      this.moderatorService.updateModerator(this.editingModerator.id, { ...payload, modified_by: userId }).subscribe({
        next: () => {
          this.message.success('Moderator updated');
          this.moderatorSaving = false;
          this.closeModeratorModal();
          this.loadModerators();
        },
        error: () => {
          this.message.error('Failed to update moderator');
          this.moderatorSaving = false;
        }
      });
    } else {
      this.moderatorService.createModerator({ ...payload, created_by: userId }).subscribe({
        next: (created: any) => {
          const currentIds = this.moderators.map(m => m.id);
          this.moderatorService.updateEventModerators(this.tabData.id, [...currentIds, created.id], userId).subscribe({
            next: () => {
              this.message.success('Moderator added');
              this.moderatorSaving = false;
              this.closeModeratorModal();
              this.loadModerators();
            },
            error: () => {
              this.message.warning('Moderator created but could not link to event');
              this.moderatorSaving = false;
              this.closeModeratorModal();
              this.loadModerators();
            }
          });
        },
        error: () => {
          this.message.error('Failed to create moderator');
          this.moderatorSaving = false;
        }
      });
    }
  }

  removeModerator(moderator: any): void {
    const userId: number = this.loginModel.value?.user?.id;
    const updatedIds = this.moderators.filter(m => m.id !== moderator.id).map(m => m.id);
    this.moderatorService.updateEventModerators(this.tabData.id, updatedIds, userId).subscribe({
      next: () => {
        this.message.success('Moderator removed from event');
        this.loadModerators();
      },
      error: () => this.message.error('Failed to remove moderator')
    });
  }
}

